import { useCallback, useEffect, useState } from 'react';
import type { DeviceHandoffMethod } from '../../../../telemetry/client';
import { QuickstartTheme } from '../../types/theme';
import { getHandoffTarget, resolveDeviceFallbackConfig } from '../../types/deviceFallbackTheme';
import { DesktopQrFallback } from './DesktopQrFallback';
import { MobileEmailFallback } from './MobileEmailFallback';
import { UnavailableFallback } from './UnavailableFallback';
import {
  mintHandoffChallenge,
  sendHandoffEmail,
  type SendHandoffEmailResult,
} from '../../../../api/modules/deviceHandoff';

interface DeviceFallbackScreenProps {
  theme: QuickstartTheme;
  /** Path slug this campaign was opened with, which the emailed link is rebuilt from. */
  urlSlug: string;
  detectedDeviceType: 'ios' | 'android' | 'desktop';
  persistentId: string | undefined;
  onHandoffRequested: (method: DeviceHandoffMethod) => void;
}

/**
 * Entry point for the device-mismatch interstitial.
 *
 * Variant is chosen from where the visitor can actually go, not merely from the device
 * they are on. Picking by detected device alone breaks platform-specific locks: on an
 * iOS-only campaign it would tell an Android visitor to switch to a computer, which that
 * campaign blocks just as hard.
 *
 * Presentation tokens come from the flat theme (same stage chrome as thank-you) via
 * resolveDeviceFallbackConfig — not a nested deviceFallback bag.
 */
export const DeviceFallbackScreen = ({
  theme,
  urlSlug,
  detectedDeviceType,
  persistentId,
  onHandoffRequested,
}: DeviceFallbackScreenProps) => {
  const handoffTarget = getHandoffTarget(theme.supportedDevices, detectedDeviceType);
  const config = resolveDeviceFallbackConfig(theme, handoffTarget);
  const [challenge, setChallenge] = useState<string | null>(null);

  const needsEmailChallenge = detectedDeviceType !== 'desktop' && handoffTarget !== 'none';

  useEffect(() => {
    if (!needsEmailChallenge) return;

    let cancelled = false;
    void mintHandoffChallenge(urlSlug).then((next) => {
      if (!cancelled) setChallenge(next);
    });
    return () => {
      cancelled = true;
    };
  }, [needsEmailChallenge, urlSlug]);

  const handleSendEmail = useCallback(
    async (email: string): Promise<SendHandoffEmailResult> => {
      if (!persistentId) {
        return { ok: false, reason: 'error' };
      }

      // Mount-time mint can fail (timeout/5xx → null). Mint lazily on submit so a
      // reload is not the only recovery.
      let activeChallenge = challenge;
      if (!activeChallenge) {
        activeChallenge = await mintHandoffChallenge(urlSlug);
        setChallenge(activeChallenge);
      }
      if (!activeChallenge) {
        return { ok: false, reason: 'error' };
      }

      const result = await sendHandoffEmail({
        email,
        urlSlug,
        persistentId,
        challenge: activeChallenge,
        originDeviceType: detectedDeviceType,
        // Identity is left out on purpose: the backend swaps in its own longer-lived
        // token because persid/persts would expire well before the mail is opened.
        query: window.location.search.replace(/^\?/, ''),
      });

      if (result.ok) {
        onHandoffRequested('email');
      }

      // Challenge is single-use; remint after every completed attempt so a retry after
      // rate_limited / error is not guaranteed to fail invalid_challenge first.
      const next = await mintHandoffChallenge(urlSlug);
      setChallenge(next);

      return result;
    },
    [persistentId, urlSlug, challenge, detectedDeviceType, onHandoffRequested],
  );

  const handleCopyLink = useCallback(() => onHandoffRequested('copy'), [onHandoffRequested]);

  if (handoffTarget === 'none') {
    return <UnavailableFallback theme={theme} config={config} />;
  }

  // Only a computer can usefully display a code for a second device to scan.
  if (detectedDeviceType === 'desktop') {
    return (
      <DesktopQrFallback
        theme={theme}
        config={config}
        persistentId={persistentId}
        originDeviceType={detectedDeviceType}
      />
    );
  }

  return (
    <MobileEmailFallback
      theme={theme}
      config={config}
      persistentId={persistentId}
      originDeviceType={detectedDeviceType}
      onSendEmail={handleSendEmail}
      onCopyLink={handleCopyLink}
    />
  );
};
